import {
  useEffect,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { BacklogConnectContext } from '@/components/backlogConnectContext'
import type { AssistAskError, AssistAskInput, AssistCard } from './assist.types'
import {
  COPILOT_PANEL_MAX_WIDTH,
  COPILOT_PANEL_MIN_WIDTH,
  COPILOT_PANEL_WIDTH_STEP,
  clampCopilotPanelWidth,
} from './copilotPanelWidth'
import { MEDIA_LG_UP, useMediaQuery } from '@/hooks/useMediaQuery'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { groupSuggestionsByQuote, selectCopilotItems } from './assist.types'
import CopilotContent from './CopilotContent'
import CopilotHeader from './CopilotHeader'
import CopilotInputBox from './CopilotInputBox'
import type { CopilotPanelUiState } from './useCopilotPanelUi'

type CopilotSidebarProps = {
  onClose: () => void
  ui: CopilotPanelUiState
  cards: AssistCard[]
  askError: AssistAskError | null
  isAsking: boolean
  onAsk: (input: AssistAskInput) => boolean
  /** When assist is off, the question box and suggestion chips do nothing. */
  askDisabled?: boolean
  /**
   * Whether the panel is open, as opposed to merely still in the tree.
   *
   * Separate from being rendered at all so there is something to animate out
   * of; `usePanelPresence` keeps the two apart.
   */
  shown: boolean
  /**
   * What the Copilot still needs before it can answer about tickets (BRAI-689).
   *
   * A slot rather than the strip's own five props, because none of what it
   * needs — the meeting row, the socket, the store — is anything this panel
   * knows about, and threading them through would make it the one component
   * that knows everything.
   */
  setupStrip?: ReactNode
  /**
   * Open the Backlog connect dialog, which the meeting screen owns.
   *
   * Offered from two places inside this panel — the setup strip and a refused
   * answer card — so it travels down as context rather than as a prop on the
   * three components in between. The dialog itself needs the meeting row and
   * the socket, neither of which this panel knows about.
   */
  onRequestConnect: () => void
}

export default function CopilotSidebar({
  onClose,
  ui,
  cards,
  askError,
  isAsking,
  onAsk,
  askDisabled = false,
  shown,
  setupStrip,
  onRequestConnect,
}: CopilotSidebarProps) {
  const { seedAnswerExpanded, seedQuoteExpanded, clearExpandSeeds } = ui
  const { t } = useUiStrings()
  const isLgUp = useMediaQuery(MEDIA_LG_UP)
  const storedWidth = useStore((state) => state.copilotPanelWidth)
  const setCopilotPanelWidth = useStore((state) => state.setCopilotPanelWidth)
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null)

  // Clamped here rather than on the way into storage: the window a width has
  // to fit is not the one it was dragged in, so a value saved on a desk
  // monitor would otherwise crowd the transcript off a laptop.
  const width = clampCopilotPanelWidth(storedWidth, window.innerWidth)

  /**
   * Resize from the panel's left edge, which is the one that moves.
   *
   * Dragging left makes the panel wider, hence the subtraction: the right
   * edge is pinned to the side of the screen, so the pointer's distance
   * travelled is the width's change with the sign flipped.
   *
   * Listeners go on the window rather than the handle. A pointer that leaves
   * a 6px strip mid-drag — which it does, constantly — would otherwise stop
   * the resize wherever it happened to be.
   */
  function startResize(event: ReactPointerEvent<HTMLDivElement>) {
    event.preventDefault()
    dragRef.current = { startX: event.clientX, startWidth: width }

    const onMove = (move: globalThis.PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      setCopilotPanelWidth(
        clampCopilotPanelWidth(drag.startWidth - (move.clientX - drag.startX), window.innerWidth)
      )
    }
    const onUp = () => {
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  /** The same edge under the arrow keys, for anyone not holding a pointer. */
  function nudgeWidth(event: React.KeyboardEvent<HTMLDivElement>) {
    const direction =
      event.key === 'ArrowLeft' ? 1 : event.key === 'ArrowRight' ? -1 : 0
    if (direction === 0) return
    event.preventDefault()
    setCopilotPanelWidth(
      clampCopilotPanelWidth(width + direction * COPILOT_PANEL_WIDTH_STEP, window.innerWidth)
    )
  }

  // Open each answer and each quoted turn once, when it first appears; later
  // toggles stay under user control, so a collapse survives both a later card and
  // a panel reopen. Seed sets live on `ui` for exactly that reason.
  // Seeded from the same list the panel draws, so a row key never points at a
  // card the reader cannot see.
  const items = useMemo(() => selectCopilotItems(cards), [cards])
  useEffect(() => {
    if (items.length === 0) {
      clearExpandSeeds()
      return
    }
    items.forEach((card, index) => {
      if (card.type !== 'answer') return
      seedAnswerExpanded(`${card.cardId}-${index}`)
    })
    items.forEach((card) => {
      if (card.type !== 'query_suggestion') return
      groupSuggestionsByQuote(card.suggestions).forEach((group) => {
        if (group.quote) seedQuoteExpanded(group.key)
      })
    })
  }, [items, clearExpandSeeds, seedAnswerExpanded, seedQuoteExpanded])

  const handleSubmit = () => {
    const text = ui.searchText.trim()
    if (!text || isAsking || askDisabled) return
    if (onAsk({ text })) {
      ui.setSearchText('')
    }
  }

  const handleAsk = (input: AssistAskInput) => {
    if (askDisabled) return
    onAsk(input)
  }

  return (
    <aside
      // Width classes are `max-lg:` only, so from `lg` up the inline width is
      // the single source of it. Below that the panel is a fixed overlay
      // covering most of the screen, where there is nothing to resize into.
      className={[
        'flex h-full min-h-0 shrink-0 flex-col border-l border-[#e2e8f0] bg-[#f8fafc] pl-px dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] max-lg:fixed max-lg:inset-y-0 max-lg:right-0 max-lg:z-40 max-lg:w-full max-lg:max-w-[360px] max-lg:shadow-xl lg:relative',
        'transition-[margin-right,transform] duration-200 ease-out motion-reduce:transition-none',
        // Below `lg` the panel is a fixed sheet, where a margin moves nothing:
        // it slides off its own right edge instead.
        shown ? 'max-lg:translate-x-0' : 'max-lg:translate-x-full',
      ].join(' ')}
      // From `lg` up it is in the flow beside the transcript, and a negative
      // margin is what lets the transcript take the room back smoothly as the
      // panel leaves. The panel keeps its width throughout — animating that
      // instead would squeeze the cards on the way out — and simply hangs past
      // the right edge, where the row's own `overflow-hidden` clips it.
      style={isLgUp ? { width: `${width}px`, marginRight: shown ? 0 : `-${width}px` } : undefined}
      data-testid="copilot-sidebar"
      aria-label="Ai Copilot"
    >
      {isLgUp ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t.copilotResizeLabel}
          aria-valuenow={width}
          aria-valuemin={COPILOT_PANEL_MIN_WIDTH}
          aria-valuemax={COPILOT_PANEL_MAX_WIDTH}
          tabIndex={0}
          data-testid="copilot-resize-handle"
          onPointerDown={startResize}
          onKeyDown={nudgeWidth}
          // Sits over the border it moves, wider than the 1px line so it can
          // actually be caught, and reachable by keyboard for anyone who is
          // not holding a pointer at all.
          className="absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none bg-transparent transition-colors hover:bg-[var(--accent-light)] focus-visible:bg-[var(--accent-light)] focus-visible:outline-none"
        />
      ) : null}
      <CopilotHeader onClose={onClose} />
      {/* Wraps the content as well as the strip: the same offer appears on a
          refused answer card, which is four components down inside it. */}
      <BacklogConnectContext.Provider value={onRequestConnect}>
        {setupStrip}
        <CopilotContent
          cards={cards}
          askError={askError}
          isAsking={isAsking || askDisabled}
          onAsk={handleAsk}
          expandedCardIds={ui.expandedCardIds}
          onToggleAnswer={ui.toggleExpanded}
          expandedQuoteIds={ui.expandedQuoteIds}
          onToggleQuote={ui.toggleQuoteExpanded}
        />
      </BacklogConnectContext.Provider>
      <CopilotInputBox
        searchText={ui.searchText}
        onSearchTextChange={ui.setSearchText}
        onSubmit={handleSubmit}
        disabled={isAsking || askDisabled}
      />
    </aside>
  )
}
